import { isAfter, isBefore, isValid, max, parseISO } from 'date-fns';
import { FormikErrors, setIn } from 'formik';

import { ContractPriceListRequestMessage, PriceListActivationMode } from '../../../../types/ContractMessage';
import { ValidationResponse } from '../../../../types/ValidationResponse';
import { ValidationResponseCode } from '../../../../types/ValidationResponseCode';
import { addUtcDays, startOfUtcDay, startOfUtcDayIso } from '../../../common/helpers/dateFns.helper';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { translate, TranslationKeyEn } from '../../../common/helpers/translate.helper';

import { PriceListFormValue } from './types';

const fieldErrorTranslations: Record<string, Record<string, TranslationKeyEn>> = {
    REQUIRED: {
        '.validFrom': 'contracts.priceLists.validation.required.validFrom',
        '.validUntil': 'contracts.priceLists.validation.required.validUntil',
    },
    CONTRACT_START_DATE_REQUIRED: {
        '.validFrom': 'contracts.priceLists.validation.contractStartDateRequired',
    },
    INVALID_DATE_RANGE: {
        '.validUntil': 'contracts.priceLists.validation.invalidDateRange',
    },
    INVALID_DATE: {
        '.validFrom': 'contracts.priceLists.validation.invalidDate',
        '.validUntil': 'contracts.priceLists.validation.invalidDate',
    },
    PRICE_LIST_START_BEFORE_TODAY: {
        '.validFrom': 'contracts.priceLists.validation.startDateBeforeToday',
    },
    PRICE_LIST_START_BEFORE_CONTRACT_START: {
        '.validFrom': 'contracts.priceLists.validation.startDateBeforeContractStart',
    },
    PRICE_LIST_EXCEEDS_CONTRACT_VALIDITY: {
        '.validUntil': 'contracts.priceLists.validation.endDateAfterContractEnd',
    },
    DATE_OVERLAP: {
        '.validFrom': 'contracts.priceLists.validation.dateOverlap',
    },
};

export const shouldAutoApplyPriceList = (priceList: Pick<PriceListFormValue, 'validFrom' | 'validUntil'>): boolean => {
    if (!priceList.validFrom) {
        return false;
    }

    const now = new Date();
    const validFrom = new Date(priceList.validFrom);

    if (!isValid(validFrom) || isBefore(now, validFrom)) {
        return false;
    }

    if (!priceList.validUntil) {
        return true;
    }

    const validUntil = new Date(toPriceListValidUntil(priceList.validUntil) ?? '');

    if (!isValid(validUntil)) {
        return false;
    }

    return !isAfter(startOfUtcDay(now), validUntil);
};

export const getNextPriceListValidFrom = (validUntil?: string | null): string | undefined => {
    if (!validUntil) {
        return undefined;
    }

    const endDate = new Date(validUntil);

    if (!isValid(endDate)) {
        return undefined;
    }

    return startOfUtcDayIso(addUtcDays(endDate, 1));
};

export const getInitialPriceListValidFrom = ({
    contractValidFrom,
    nextAvailableValidFrom,
}: {
    contractValidFrom?: string;
    nextAvailableValidFrom?: string;
}): string | undefined => {
    const candidates = [startOfUtcDayIso(new Date()), contractValidFrom, nextAvailableValidFrom]
        .map((candidate) => (candidate ? parseISO(candidate) : null))
        .filter((candidate): candidate is Date => !!candidate && isValid(candidate));

    if (candidates.length === 0) {
        return undefined;
    }

    return startOfUtcDayIso(max(candidates));
};

export const toPriceListValidUntil = (validUntil?: string | null): string | undefined =>
    validUntil ? startOfUtcDayIso(validUntil) : undefined;

export const formatPriceListDate = (date: string): string => formatDate({ date, timeZone: 'UTC' });

export const toPriceListRequestPayload = (
    priceLists: PriceListFormValue[],
    activationMode: PriceListActivationMode
): ContractPriceListRequestMessage[] =>
    priceLists.map((priceList) => ({
        originalFileId: priceList.fileId,
        validFrom: priceList.validFrom ?? undefined,
        validUntil: toPriceListValidUntil(priceList.validUntil),
        activeOnApproval:
            activationMode === PriceListActivationMode.AUTO
                ? shouldAutoApplyPriceList(priceList)
                : priceList.activeOnApproval,
    }));

export const applyPriceListValidationErrors = ({
    response,
    index,
    setFieldError,
    setFieldTouched,
}: {
    response: ValidationResponse;
    index: number;
    setFieldError: (field: string, message: string) => void;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
}) => {
    if (response.code !== ValidationResponseCode.VALIDATION_FAILED) {
        return;
    }

    response.errors.forEach((error) => {
        if (!error.path.startsWith('.')) {
            return;
        }

        const translationKey = fieldErrorTranslations[error.code]?.[error.path];

        if (!translationKey) {
            return;
        }

        const fieldName = `priceLists[${index}]${error.path}`;
        setFieldTouched(fieldName, true, false);
        setFieldError(fieldName, translate(translationKey));
    });
};

export const mapPriceListValidationErrors = <TValues>(
    response: ValidationResponse,
    index: number
): FormikErrors<TValues> => {
    let errors: FormikErrors<TValues> = {};

    if (response.code !== ValidationResponseCode.VALIDATION_FAILED) {
        return errors;
    }

    response.errors.forEach((error) => {
        if (!error.path.startsWith('.')) {
            return;
        }

        const translationKey = fieldErrorTranslations[error.code]?.[error.path];

        if (!translationKey) {
            return;
        }

        errors = setIn(errors, `priceLists[${index}]${error.path}`, translate(translationKey));
    });

    return errors;
};
